import { PriceInputValue } from "@/features/invoices/app-price-input";
import PriceUtils from "@/features/ui/utils/price-utils";
import { isDeductibleCredit } from "@/features/invoices/credit-invoices/credit-invoice.type";
import type { ProformaInvoiceFormFields } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";

/**
 * 3자무역 — 2026-10-30 부터 수수료율을 곱하지 않는다.
 * 인보이스 총액(수수료가 포함된 인보이스 금액)을 담당자가 그대로 입력하고, 송금 필요 금액은
 * 총액 - 차감 크레딧 - 사용 디파짓 이다. 서버에 저장된 cross_trade_fee_rate 는 정보로만 남는다.
 * 아래 withCrossTradeFee / withCrossTradeFeeDeposit 는 예전 호출처를 위해 남겨 두되 값을 바꾸지 않는다.
 */
export function withCrossTradeFee(
  base: PriceInputValue,
  _crossTradeFeeRate?: number | null
): PriceInputValue {
  return base;
}

/**
 * 총 송금 필요 금액 — 담당자가 직접 입력한 값(totalRequiredAmount)이 있으면 그 값, 없으면 자동 계산값.
 * 인보이스가 상품별로 수수료를 반올림하는 등 합계식과 어긋날 때 담당자가 인보이스 금액에 맞춰 고친다.
 * 서버는 식과 같은지는 보지 않고 0 이상·총액 이하인지만 본다.
 */
export function resolveTotalRequiredAmount(
  manual: PriceInputValue | undefined,
  computed: PriceInputValue
): PriceInputValue {
  if (manual?.value === undefined) {
    return computed;
  }
  return { ...computed, value: manual.value };
}

/** 직접 입력한 송금 필요 금액이 자동 계산값과 다른지(1센트 넘게). */
export function isTotalRequiredAmountManual(
  manual: PriceInputValue | undefined,
  computed: PriceInputValue
): boolean {
  return (
    manual?.value !== undefined &&
    Math.abs(manual.value - (computed.value ?? 0)) > 0.005
  );
}

/**
 * 저장된 송금 필요 금액을 폼에 실을 때 — 자동 계산값과 같으면 비워 두고(자동), 다르면 직접 입력값으로 둔다.
 * 그래야 담당자가 고쳐 둔 금액이 수정 화면에서 자동 계산값으로 덮이지 않는다.
 */
export function storedTotalRequiredAmount(
  stored: PriceInputValue | undefined,
  computed: PriceInputValue
): PriceInputValue | undefined {
  return isTotalRequiredAmountManual(stored, computed) ? stored : undefined;
}

/**
 * 프로포마 송금 필요 금액(자동 계산) = 수수료 대상 금액(총액 - 차감 크레딧) - 사용 디파짓.
 * (3자무역 수수료는 더 이상 곱하지 않는다 — 총액이 인보이스 금액 그대로다)
 */
export function proformaRequiredAmount(
  feeBaseAmount: PriceInputValue,
  usedDeposit: PriceInputValue | undefined,
  _crossTradeFeeRate: number | null | undefined,
  currency: NonNullable<ProformaInvoiceFormFields["currency"]>
): PriceInputValue {
  return PriceUtils.minus([feeBaseAmount, usedDeposit ?? { currency }], currency);
}

/**
 * 프로포마 폼의 금액 — 총액은 담당자가 입력한 totalAmount(없으면 옛 폼의 브랜드별 금액 합),
 * 송금 필요 금액 = 총액 - 차감 크레딧(에이전시 소유 제외) - 사용 디파짓.
 * 정보 패널과 금액 패널이 같은 값을 쓰도록 한곳에서 계산한다.
 */
export function proformaRequiredAmounts(
  values: Pick<
    ProformaInvoiceFormFields,
    "proformaInvoiceDetailList" | "usedCreditInvoices" | "usedDeposit" | "crossTradeFeeRate"
  > & { totalAmount?: PriceInputValue },
  currency: NonNullable<ProformaInvoiceFormFields["currency"]>
) {
  const amount: PriceInputValue =
    values.totalAmount?.value !== undefined
      ? { currency, value: values.totalAmount.value }
      : PriceUtils.sum(
          (values.proformaInvoiceDetailList ?? []).map((pDetail) => pDetail.amount),
          currency
        );
  const deductibleCredit = PriceUtils.sum(
    values.usedCreditInvoices
      .filter(isDeductibleCredit)
      .map((cDetail) => cDetail.amount),
    currency
  );
  const feeBaseAmount = PriceUtils.minus([amount, deductibleCredit], currency);
  const requiredAmount = PriceUtils.minus(
    [feeBaseAmount, values.usedDeposit],
    currency
  );
  const crossTradeFee: PriceInputValue = { currency, value: 0 };
  return { amount, baseRequiredAmount: requiredAmount, requiredAmount, crossTradeFee };
}

/** OC 디파짓 — 수수료를 곱하지 않는다(인보이스의 디파짓 금액을 그대로 입력한다). 옛 호출처용. */
export function withCrossTradeFeeDeposit<
  T extends { amount?: PriceInputValue; requiredAmount?: PriceInputValue }
>(
  totalRequiredAmount: PriceInputValue,
  details: T[],
  _crossTradeFeeRate?: number | null
): { totalRequiredAmount: PriceInputValue; details: T[] } {
  return { totalRequiredAmount, details };
}

/**
 * 연결한 OC 들의 예상 잔여 디파짓 합(OC 헤더 기준). 시즌 프로포마가 쓸 수 있는 디파짓 상한.
 * 수정 화면에서는 이 프로포마가 이미 쓴 디파짓을 더해서 넘긴다(서버가 되돌린 뒤 다시 깎으므로).
 */
export function remainingDepositOf(
  orderConfirmationInvoices: { expectedRemainingDepositAmount?: PriceInputValue }[],
  currency: NonNullable<ProformaInvoiceFormFields["currency"]>,
  ownUsedDeposit?: PriceInputValue
): PriceInputValue {
  return PriceUtils.sum(
    [
      ...orderConfirmationInvoices.map((oc) => oc.expectedRemainingDepositAmount),
      ownUsedDeposit,
    ],
    currency
  );
}

/** 연결한 오더들의 확정 금액 합 — 새 프로포마 총액의 초기값(담당자가 인보이스 금액으로 고친다). */
export function confirmedAmountOf(
  orderSheetSets: { confirmedAmount?: number | null }[],
  currency: NonNullable<ProformaInvoiceFormFields["currency"]>
): PriceInputValue | undefined {
  if (orderSheetSets.length === 0) {
    return undefined;
  }
  return PriceUtils.sum(
    orderSheetSets.map((oss) => ({ currency, value: oss.confirmedAmount ?? 0 })),
    currency
  );
}

// 3자무역을 켰는데 3자무역 회사를 안 골랐으면 업로드·저장을 막는다(발행사가 부티크인 채 수수료만 붙지 않게)
export const CROSS_TRADER_REQUIRED_MESSAGE = "3자무역 회사를 골라 주세요.";

export function isCrossTraderMissing(values: {
  isCrossTrade?: boolean;
  issuedBy?: { type?: string };
}): boolean {
  return !!values.isCrossTrade && values.issuedBy?.type !== "CROSS_TRADER";
}
